import { Button, Empty, Input, InputNumber, Table, Typography } from '@arco-design/web-react'
import { IconDelete, IconPlus } from '@arco-design/web-react/icon'
import { Pill } from '@/utils/render'
import type { GroupRow } from '../ratioSettingUtils'
import { createRatioRowId } from '../ratioSettingUtils'

interface Props {
  rows: GroupRow[]
  onChange: (rows: GroupRow[]) => void
}

export default function GroupRatioEditor({ rows, onChange }: Props) {
  const updateRow = (id: string, patch: Partial<GroupRow>) => {
    onChange(rows.map((row) => (row.id === id ? { ...row, ...patch } : row)))
  }

  const columns = [
    {
      title: '分组 Key',
      dataIndex: 'key',
      render: (_: unknown, row: GroupRow) => (
        <Input
          placeholder="default / vip / svip"
          value={row.key}
          onChange={(value) => updateRow(row.id, { key: value })}
        />
      ),
    },
    {
      title: '展示名',
      dataIndex: 'displayName',
      render: (_: unknown, row: GroupRow) => (
        <Input
          placeholder="默认分组 / VIP 分组"
          value={row.displayName}
          onChange={(value) => updateRow(row.id, { displayName: value })}
        />
      ),
    },
    {
      title: '用户可见',
      width: 120,
      render: (_: unknown, row: GroupRow) =>
        row.displayName.trim() ? (
          <Pill variant="success">可见</Pill>
        ) : (
          <Pill variant="neutral">仅路由/计费</Pill>
        ),
    },
    {
      title: '基础倍率',
      dataIndex: 'ratio',
      width: 180,
      render: (_: unknown, row: GroupRow) => (
        <InputNumber
          min={0}
          step={0.1}
          className="!w-full"
          value={row.ratio}
          onChange={(value) =>
            updateRow(row.id, { ratio: value == null || !Number.isFinite(value) ? 0 : value })
          }
        />
      ),
    },
    {
      title: '操作',
      width: 90,
      render: (_: unknown, row: GroupRow) => (
        <Button
          type="text"
          status="danger"
          icon={<IconDelete />}
          aria-label="删除分组"
          onClick={() => onChange(rows.filter((item) => item.id !== row.id))}
        />
      ),
    },
  ]

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <Typography.Text bold>分组倍率与展示名</Typography.Text>
          <Typography.Paragraph type="secondary" className="!text-xs !mb-0">
            分组 Key 写入 GroupRatio，用于渠道路由与基础计费倍率；展示名写入 UserUsableGroups，仅决定普通用户可见/可选时的文案。
            展示名为空时，该分组仍可作为渠道分组计费，但不会自动出现在用户可用分组里。
          </Typography.Paragraph>
        </div>
        <Button
          type="outline"
          icon={<IconPlus />}
          onClick={() => onChange([...rows, { id: createRatioRowId('group'), key: '', displayName: '', ratio: 1 }])}
        >
          添加分组
        </Button>
      </div>
      <Table
        rowKey="id"
        size="small"
        border={false}
        columns={columns}
        data={rows}
        pagination={false}
        noDataElement={<Empty description="暂无分组，点击右上角添加" />}
      />
    </div>
  )
}
